import {NextRequest, NextResponse} from "next/server";
import cryptoRandomString from "crypto-random-string";
import {SERVICES} from "../common/constants/routes";
import {createPictureUri} from "../components/fundamentals/CCOPicture";
import {sanitizeRelativeUrl, sanitizeUrlValue} from "./sanitizerUtil";

export type Triangle = [{ x: number, y: number }, { x: number, y: number }, {
    x: number,
    y: number
}, {
    x: number,
    y: number
}];

/**
 * Returns the URL of the target. If the target is a string, it is returned as is.
 * If the target is an object, the selfUrl property is returned if it is a string and does not start with "Content[".
 * @param target
 */
export function getUrl(target: any) {
    if (typeof target === 'string') {
        return target;
    } else {
        const url = target?.selfUrl;
        return url && (typeof url === 'string') && !url.startsWith('Content[') ? url : "#";
    }
}

export function resolveLinkWithPlaceholder(target: any, storages: any[] | null, elementId: string,
                                           pageId: string) {
    if (!target) {
        return "#";
    }
    let checkedTarget = getUrl(target);
    const regex = /{{(.*?)}}/g;
    let url = decodeURIComponent(checkedTarget);
    if (!url.match(regex)) {
        return checkedTarget;
    }
    const matches = url.match(regex);
    if (matches) {
        matches.forEach((match: string) => {
            const placeholder = match.replace(/{{|}}/g, '').trim();
            let key = placeholder;
            let defaultValue = '';
            if (placeholder.includes(':')) {
                let placeholderSettings = placeholder.split(':');
                key = placeholderSettings[0];
                defaultValue = sanitizeUrlValue(placeholderSettings[1]);
            }
            let value = null;
            if (storages) {
                storages.forEach(storage => {
                    if (storage[key]) {
                        value = storage[key];
                    }
                })
            }
            if (key === "elementId" && elementId !== undefined) {
                value = elementId;
            }
            if (key === "pageId" && pageId !== undefined) {
                value = pageId;
            }
            let parameterEndValue = value ? sanitizeUrlValue(value) : defaultValue;
            url = url.replace(match, parameterEndValue);
        });
        return encodeURI(url);
    }
    return checkedTarget;
}

/**
 * Returns the URL of the target with an anchor appended. If the target is a string, it is returned as is.
 * If the target is an object, the selfUrl property is returned if it is a string and does not start with "Content[".
 * @param target
 * @param anchor
 */
export function getUrlWithAnchor(target: any, anchor: string) {
    let url = getUrl(target);
    if (url.indexOf('#') > -1) {
        url = url.substring(0, url.indexOf('#')) + '#' + anchor;
    } else {
        return url + '#' + anchor;
    }
    return url;
}

/**
 * Resolves the root segment based on the given host header.
 * @param {string} hostHeader - The host header value to resolve.
 * @return {undefined|string} - The resolved root segment or undefined if not found.
 */
export function resolveRootSegmentByHost(hostHeader: string): string {
    // add lines for each sub-domain. A sub-domain may have three or even more host-names.
    // At least it should be one for DEV, STAGE and PROD.
    // Have in mind, that these host-names are only in use for deliver-RAE!!!
    const json = {
        'portal': ['portal:3000', 'www.dev.cm.soskd.org', 'www.stage.cm.soskd.org',
            'www.sos-kinderdorf.de', 'www.prod.cm.soskd.org'],
        'start': ['start.dev.cm.soskd.org', 'start.stage.cm.soskd.org',
            'www.testament-schreiben.com', 'start.prod.cm.soskd.org'],
        'campus': ['campus.dev.cm.soskd.org', 'campus.stage.cm.soskd.org',
            'www.sos-kinderdorf-campus.de', 'campus.prod.cm.soskd.org'],
        'info': ['info.dev.cm.soskd.org', 'info.stage.cm.soskd.org', 'www.aufarbeitung-sos.de',
            'info.prod.cm.soskd.org'],
        'iuw': ['iuw.dev.cm.soskd.org', 'iuw.stage.cm.soskd.org', 'www.ichundwir.de',
            'iuw.prod.cm.soskd.org'],
        'uaa': ['uaa.dev.cm.soskd.org', 'uaa.stage.cm.soskd.org', 'www.anerkennung-sos.de',
            'uaa.prod.cm.soskd.org'],
        'de': ['de.dev.cm.soskd.org', 'de.stage.cm.soskd.org', 'www.sos-kinderdorf-stiftung.de',
            'de.prod.cm.soskd.org'],
        'f49': ['f49.dev.cm.soskd.org', 'f49.stage.cm.soskd.org', 'www.forum49.de',
            'f49.prod.cm.soskd.org']
    }
    const map = new Map(Object.entries(json));
    let retVal = "";
    map.forEach((value, key) => {
        value.map(v => {
            if (v === hostHeader) {
                retVal = key;
            }
        });
    })
    return retVal;
}

/**
 * Returns true if root segment is resolved within the domain name in the current environment
 */
export function isPortalDelivery(domain: string): boolean {
    const rootSegmentResolvingDomains = [
        'www.dev.cm.soskd.org',
        'www.stage.cm.soskd.org',
        'www.prod.cm.soskd.org',
        'sos-kinderdorf.de',
        'www.sos-kinderdorf.de'
    ]
    return rootSegmentResolvingDomains.some((element) => domain.includes(element));
}

export function getShortUrlRootSlug(rootSlug: string) {
    if (rootSlug && (
        rootSlug === 'portal' ||
        rootSlug === 'start' ||
        rootSlug === 'campus' ||
        rootSlug === 'info' ||
        rootSlug === 'iuw' ||
        rootSlug === 'f49' ||
        rootSlug === 'uaa' ||
        rootSlug === 'de')) {
        return "";
    } else {
        return rootSlug ? rootSlug : "";
    }
}

export function repairDoubleRootSegment(segmentByHost: string, path: string) {
    if (path) {
        if (segmentByHost && segmentByHost !== "") {
            const search = segmentByHost + "/" + segmentByHost;
            path = path.replace(search, segmentByHost);
        }
        path = path.replaceAll("//", "/");
    }
    return path;
}

function getTokenFromHeader(req: NextRequest) {
    //@ts-ignore
    const cookieHeader = req.headers.cookie || '';
    const csrfToken = cookieHeader
                          .split(';')
                          .map((part: string) => part.trim())
                          .find((part: string) => part.startsWith('csrf_token='))
                          ?.split('=')[1] || '';
    return csrfToken;
}

export function setCsrfCookie(req: NextRequest, res: NextResponse): string {
    const existingToken = getTokenFromHeader(req);
    if (existingToken) return existingToken;

    const url = req.url;
    if (!url ||
        url.includes(".css.map") ||
        url.includes('/_next/static/') ||  // Bilder oder sonstige Service-Requests vom NextJs
        url.includes("service-worker.js") ||
        url.includes("/error-") ||
        url.includes("/api/") || // API-Requests
        url.includes('/caas/') || // caas-Schnittstelle
        url.includes('.svg') || // die SVGs aus dem Spendenformular
        (url.includes('.json') && !url.includes("_next/data")) // vor allem wegen: /.well-known/appspecific/com.chrome.devtools.json
    ) {
        return existingToken || '';
    } else {
        const newToken = cryptoRandomString({
                                                length: 100,
                                                characters: '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
                                            });
        // @ts-ignore
        res.setHeader("set-cookie", `csrf_token=${newToken}; path=/; samesite=Strict; httponly;`);
        return newToken;
    }
}

export function getQueryParam(search: string, name: string): string {
    if (!search) {
        return "";
    }
    const reg = new RegExp(`(^|&|\\?)${name}=([^&]*)(&|$)`, "i");
    const r = search.substring(1).match(reg);
    if (r != null) {
        return decodeURIComponent(r[2]);
    }
    return "";
}

export function removeQueryParams(search: string, paramNames: string[]): string {
    let searchArray = search.split("&");
    paramNames.forEach(
        name => searchArray = searchArray.filter(paramEntry => !paramEntry.startsWith(name)));
    return searchArray.join("&");
}

/**
 * This method is used to add prefix and title to the data url so that the link is caught by middleware
 */
export function generateDownloadUrl(contentId?: string, dataTitle?: string): string {
    let encodedTitle = encodeURIComponent(dataTitle ?? 'document');
    return SERVICES.DOWNLOAD + `/${encodedTitle}?id=${contentId ?? ''}`
}

export function generateDownloadTargetFromUrl(target: any): string | null {
    const match = /coremedia:\/\/\/cap\/content\/(\d+):CMDownload(.*?)/
    const documentTitle = target.teaserTitle ? encodeURIComponent(target.teaserTitle) : 'document';

    if (target && target.selfUrl && match.exec(target.selfUrl)) {
        return target.selfUrl.replace('coremedia:///cap/content/',
                                      `${SERVICES.DOWNLOAD}/${documentTitle}?id=`).replace(
            ':CMDownload', '');
    }
    return null;
}

export function isTargetDownload(target: any): boolean {
    return target && target.type === undefined &&
           (stringify(target) as string).includes('/download')
}

function stringify(value: any) {
    return (typeof value === 'string' || value instanceof String) ? value : value.toString();
}

export function getIdFromHref(href: string): string {
    const match = /\/content\/(.*?):CM*/.exec(href);
    return match && match.length > 1 ? match[1] : '';
}

export function getIdFromDataHref(href: string) {
    const match = /\/caas\/v1\/media\/(.*?)\/data*/.exec(href);
    return match && match.length > 1 ? match[1] : '';
}

export function generateNextImageUrl(innerImgUrl: string, ratio: string, width: number): string {
    const origin =
        typeof window !== 'undefined' && window.location.origin
            ? window.location.origin
            : '';
    return origin + createPictureUri(width.toString(), innerImgUrl, ratio) as string;
}

/**
 * Redirects the browser to the given URL.
 * router.push does not work, if the former request comes from an external source, like saferpay.
 * Thus, we use oldschool javascript redirect for that.
 *
 * @param url to redirect to
 */
export function redirectTo(url: string) {
    if (typeof window !== 'undefined') {
        url = sanitizeRelativeUrl(url);
        window.location.href = url;
    }
}

export function getRouteUrl(siteRoot: string, routeUrl: string) {
    let path = siteRoot + routeUrl;
    path = repairDoubleRootSegment(siteRoot, path);
    return path;
}

export const bytesToSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return 'n/a'
    const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))), 10)
    if (i === 0) return `${bytes} ${sizes[i]})`
    return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`
}

export const extractSegmentFromSelfUrl = (selfUrl: string): string | undefined => {
    if (!selfUrl || typeof selfUrl.split !== "function") return undefined;
    const urlParts = selfUrl.split('/');
    return urlParts.length > 1 ? urlParts[1] : undefined;
}

export function calculateMouseSafeArea(event: any,
                                       setMouseSafeArea: (area: Triangle | null) => void) {
    const element = event.target;
    const findNearestButton = (element: any) => {
        if (element.tagName === 'SPAN') {
            return element;
        }
        const nearestButton = element.querySelector('span');
        if (nearestButton) {
            return nearestButton;
        }
        return null;
    };
    const nearestButton = findNearestButton(element);
    const buttonRect = nearestButton ? nearestButton.getBoundingClientRect() :
        element.getBoundingClientRect();

    const menuItemTopLeftX = buttonRect.left;
    const menuItemTopLeftY = buttonRect.top - 5;

    const menuItemBottomRightX = buttonRect.right;
    const menuItemBottomRightY = buttonRect.bottom + 5;

    const subLinksMenuColumnElement = document.getElementById('menu-sub-links-box');
    if (subLinksMenuColumnElement) {
        const rect = subLinksMenuColumnElement.getBoundingClientRect();

        const deepMenuTop = rect.top;
        const deepMenuBottom = rect.bottom;
        const deepMenuLeft = rect.left;

        setMouseSafeArea([
                             {x: deepMenuLeft, y: deepMenuTop},
                             {x: deepMenuLeft, y: deepMenuBottom},
                             {x: menuItemBottomRightX, y: menuItemBottomRightY},
                             {x: menuItemTopLeftX, y: menuItemTopLeftY}]);
    } else {
        return
    }
}